import {put, call, all} from 'redux-saga/effects';
import Comment from "../../entities/comment";
import * as Actions from "../actions/commentActions";
import * as idb from '../../lib/idbd';
import * as api from '../../rest/api';
import {processQueue} from "../../queues/processQueue";
import newHRTimestamp from "../../lib/picos/timestamp";
import {ensureOperationId} from '../../lib/pendingRows';
import {tabSession} from '../../Auth/session';

// Inlined to reduce circular dependencies
function getCurrentUserId() {
    return sessionStorage.getItem('userId');
}

// Inlined to reduce circular dependencies
function getCurrentFirmId() {
    return sessionStorage.getItem('firmId');
}

type CommentAction = {
    commentId: string;
    comment: Comment;
    comments: Comment[];
    sentAddedOn?: number;
    onQueued?: (saved: boolean) => void;
};

export function* fetchOneComment(action: CommentAction) {
	const session = tabSession.captureGeneration();
	try {
		const comment: Comment = yield call(api.findOne, 'comment', action.commentId);
		session.assertCurrent();

		// Now we can update the user interface
		yield put({type: Actions.COMMENT_FETCH_ONE_SUCCESS, commentId: action.commentId, comment, sentAddedOn: action.sentAddedOn});
	}
	catch(err) {
		if (session.signal.aborted) return;
		yield put({type: Actions.COMMENT_FETCH_ONE_FAILURE, commentId: action.commentId, status: 500, err});

		console.error('Error fetching from database', err);
	}
}

/**
 * @param {CommentAction} action
 */
export function* saveComment(action: CommentAction) {
    const session = tabSession.captureGeneration();
    let queueReported = false;
    const reportQueued = (saved: boolean) => {
        if (!queueReported) { queueReported = true; action.onQueued?.(saved); }
    };
    try {
        // console.debug('⮂ COMMENT_SAVE saga', action.comment?.id);

		if (action.comment === undefined || action.comment === null) {
			console.error('saveComment received a null');
			throw new Error('saveComment received a null');
		}

		if (!(action.comment instanceof Comment)) {
			action.comment = new Comment(action.comment);
		}
		// OWA-1018: the reducer showed this save at dispatch, stamped after the version this tab holds
		// (OWA-1083), marked pending under this queue key
		const dispatchedAddedOn = action.comment.addedOn;

		try {
			// Save to IndexedDB
			ensureOperationId(action.comment);
			const addResult = yield call(idb.add, 'comment', action.comment);
			if (addResult.isErr()) {
				console.error(`⮂ saveComment: IDB write failed: ${addResult.unwrapErr().message}`);
				reportQueued(false);
				session.assertCurrent();
				// The save never reached the queue: its row returns to the latest accepted version
				yield put(Actions.revertCommentSave(action.comment, {sentAddedOn: dispatchedAddedOn}));
				yield put({type: Actions.COMMENT_SAVE_FAILURE, comment: action.comment, status: 500, message: addResult.unwrapErr().message});
				return;
			}
			session.assertCurrent();
			reportQueued(true);

			// Now we can update the user interface: the queued copy replaces the row shown at dispatch
			// (the queue may have stamped it again)
			yield put({type: Actions.COMMENT_SAVE, comment: action.comment, dispatchedAddedOn, queuedAt: performance.now()});

			// A save never deletes autosaved drafts (OWA-958). The editor that wrote a draft
			// removes it when it saves; a draft typed somewhere else may be newer than this copy.
			session.assertCurrent();

			// Let the queue know to process
			yield call(processQueue);
		}
		catch(err) {
			if (session.signal.aborted) { reportQueued(false); return; }
			// OWA-1018: a save that never reached the queue returns its row to the latest accepted version
			if (!queueReported) yield put(Actions.revertCommentSave(action.comment, {sentAddedOn: dispatchedAddedOn}));
			reportQueued(false);
			yield put({type: Actions.COMMENT_SAVE_FAILURE, comment: action.comment, status: 500, err});

			console.error('Error saving to database', err);
		}
    } finally { reportQueued(false); }
}

/**
 * TODO: Update for saveMany
 * @param {CommentAction} action
 * @param {Comment[]} action.comments
 */
export function* saveManyComments(action: CommentAction) {
    const session = tabSession.captureGeneration();
    let queueReported = false;
    const reportQueued = (saved: boolean) => {
        if (!queueReported) { queueReported = true; action.onQueued?.(saved); }
    };
    try {
        // console.debug('⮂ COMMENT_SAVE_MANY saga', action.comments.map(comment => comment.id).join(','));

        if (!Array.isArray(action.comments)) {
            console.error('saveManyComments expected an array, received', action.comments);
            throw new Error('saveManyComments expected an array');
        }
		// OWA-1018: the reducer showed these saves at dispatch (see saveComment), under these queue keys
		const dispatchedAddedOns: number[] = action.comments.map(comment => comment?.addedOn);
		const revertAll = () => action.comments.map((comment, i) =>
			put(Actions.revertCommentSave(comment, {sentAddedOn: dispatchedAddedOns[i]})));

		try {
			const toSave: Comment[] = [];

			// Save each item to IndexedDB
			for(let i = 0; i < action.comments.length; i++) {
				const comment = action.comments[i];
				comment.persisted = false;
				ensureOperationId(comment);
				toSave.push(comment);
			}

			const addResult = yield call(idb.add, 'comment', toSave);
			if (addResult.isErr()) {
				console.error(`⮂ saveManyComments: IDB write failed: ${addResult.unwrapErr().message}`);
				reportQueued(false);
				session.assertCurrent();
				// The saves never reached the queue: their rows return to the latest accepted versions
				yield all(revertAll());
				yield put({type: Actions.COMMENT_SAVE_MANY_FAILURE, comments: action.comments, status: 500, message: addResult.unwrapErr().message});
				return;
			}
			session.assertCurrent();
			reportQueued(true);

			// Now we can update the user interface: the queued copies replace the rows shown at dispatch
			yield put({type: Actions.COMMENT_SAVE_MANY, comments: toSave, dispatchedAddedOns, queuedAt: performance.now()});
			session.assertCurrent();

			// Let the queue know to process
			yield call(processQueue)
		}
		catch(err) {
			if (session.signal.aborted) { reportQueued(false); return; }
			// OWA-1018: saves that never reached the queue return their rows to the latest accepted versions
			if (!queueReported) yield all(revertAll());
			reportQueued(false);
			yield put({type: Actions.COMMENT_SAVE_MANY_FAILURE, comments: action.comments, status: 500, err});

			console.error(`Error saving ${action.comments.length} comments to database`, err);
		}
    } finally { reportQueued(false); }
}

/**
 * @param {CommentAction} action
 */
export function* removeComment(action: CommentAction) {
    const session = tabSession.captureGeneration();
    const userId = getCurrentUserId();
    let queueReported = false;
    const reportQueued = (saved: boolean) => {
        if (!queueReported) { queueReported = true; action.onQueued?.(saved); }
    };
	if (!userId || action.comment === undefined || action.comment === null || !(action.comment instanceof Comment)) {
			console.error('removeComment expected type Comment, received', action.comment);
			reportQueued(false);
			throw new Error('removeComment expected type Comment');
		}

	try {
		// Mark as deleted. The stamp follows the version this tab holds (the reducer raised the clock, OWA-1083).
		action.comment.status = 'D';
		action.comment.addedOn = newHRTimestamp();
		action.comment.addedBy = userId;

		// Save to IndexedDB
		ensureOperationId(action.comment);
		const addResult = yield call(idb.add, 'comment', action.comment);
		session.assertCurrent();
		if (addResult.isErr()) {
			console.error(`⮂ removeComment: IDB write failed: ${addResult.unwrapErr().message}`);
			reportQueued(false);
			yield put({type: Actions.COMMENT_REMOVE_FAILURE, comment: action.comment, status: 500, message: addResult.unwrapErr().message});
			return;
		}

		// Now we can update the user interface
		yield put({type: Actions.COMMENT_REMOVE, comment: action.comment});
		session.assertCurrent();

        reportQueued(true);

		// Let the queue know to process
		yield call(processQueue)
	}
	catch(err) {
		if (session.signal.aborted) return;
		yield put({type: Actions.COMMENT_REMOVE_FAILURE, comment: action.comment, status: 500, err});

		console.error('Error deleting from database', err);
	}
    finally { reportQueued(false); }
}

/**
	* Restore the last version of this Comment.
	*
	* @param {CommentAction} action
	*/
export function* restoreComment(action: CommentAction) {
    const session = tabSession.captureGeneration();
    const userId = getCurrentUserId();
    if (!userId) {
        console.error('restoreComment expected getCurrentUserId() to return a value');
        return;
    }

	if (action.comment === undefined || action.comment === null) {
        console.error('restoreComment expected a Comment, received null');
        throw new Error('restoreComment expected a Comment, received null');
    }
    else if (!(action.comment instanceof Comment)) action.comment = new Comment(action.comment); 

	try {
		// Mark as restored. The stamp follows the version this tab holds (the reducer raised the clock, OWA-1083).
		action.comment.status = 'U';
		action.comment.addedOn = newHRTimestamp();
		action.comment.addedBy = userId;
		action.comment.restore = true;

		// Save to IndexedDB
		ensureOperationId(action.comment);
		const addResult = yield call(idb.add, 'comment', action.comment);
		session.assertCurrent();
		if (addResult.isErr()) {
			console.error(`⮂ restoreComment: IDB write failed: ${addResult.unwrapErr().message}`);
			yield put({type: Actions.COMMENT_RESTORE_FAILURE, comment: action.comment, status: 500, message: addResult.unwrapErr().message});
			return;
		}

		// Now we can update the user interface
		yield put({type: Actions.COMMENT_RESTORE, comment: action.comment});
	}
	catch(err) {
		if (session.signal.aborted) return;
		yield put({type: Actions.COMMENT_RESTORE_FAILURE, comment: action.comment, status: 500, err});

		console.error('Error restoring from database', err);
	}
}